// GENERAL
import React, { useRef, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
	slideUpModalVariants,
	slideUpModalOverlayVariants,
	slideOutMenuOverlayVariant,
	slideOutMenuVariants,
} from "../../../../motionVariants";

// HOOKS
import useHostName from "../../../../hooks/useHostName";
// CONTEXTS
import { useDataTableContext } from "../../../../hooks";

// COMPONENTS
import {
	ApprovalHeader,
	ApprovalOrderDetails,
	ApprovalNotesSection,
	ApprovalNotesModal,
	ApprovalOrderItems,
	ApprovalDecisionsModal,
	EnterPoNumberModal,
} from "./index";
import { EventLog, LoadingMessage, SupplyPortal } from "../../../../components";
// UTILS
import {
	clearLocalStorage,
	formatDateForDetailsBox,
	formatCurrency,
} from "../../../../utils";

// VIEW MODELS
import { useApprovalsModalViewModel } from "./viewModels";

// TYPES
import { ApprovalsModalProps, ModalType } from "./types";
import { AdminSalesOrderNote } from "../../types";

const ApprovalsModal: React.FC<ApprovalsModalProps> = ({
	isOpen,
	onClose,
	hasBorder = false,
	borderColor = "primary",
	updateRowData,
}) => {
	const modalRef = useRef<HTMLDivElement>(null);

	///// VIEW MODEL /////
	const {
		orderDetails,
		salesOrderItems,
		orderAddressDetails,
		setRefetchData,
		refetchData,
		aclPermissions,
		fields,
		selectedSingleItems,
		selectedBundles,
		salesOrderNotes,
		showErrorMessage,
		showLoadingState,
		approvalDecisions,
		purchaseOrderDetails,
	} = useApprovalsModalViewModel();

	///// HOOKS AND CONTEXTS /////
	const { activeUuid, setActiveRowUuid, setDeactivatedQuickMenuIds } =
		useDataTableContext();
	const hostName = useHostName().hostName;

	///// STATES /////
	const [isViewAllNotesOpen, setIsViewAllNotesOpen] =
		useState<boolean>(false);
	const [activeModal, setActiveModal] = useState<ModalType | null>(null);
	const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
	const [isEventLogOpen, setIsEventLogOpen] = useState<boolean>(false);

	// we needed to update state (setActiveRowUuid) in response to a prop change (isOpen).
	// Attempting to update state directly during the render phase causes React to throw a
	// warning because it can lead to inconsistent UI and unpredictable behavior
	// this needs to be done in order for the real time update in the data table to work
	// from the Approvals modal
	// useEffect handles side effects related to opening and closing the modal
	useEffect(() => {
		if (isOpen) {
			// Side effect: Update state based on prop change
			setActiveRowUuid(activeUuid);
			// Side effect: Modify DOM by adding a class
			document.body.classList.add("overflow-hidden");
		} else {
			// Clean up when modal is closed
			document.body.classList.remove("overflow-hidden");
		}
		// Clean up when component unmounts
		return () => {
			document.body.classList.remove("overflow-hidden");
		};
		// Dependencies ensure effect runs when these values change
	}, [isOpen, activeUuid, setActiveRowUuid]);

	///// CLASS NAMES /////
	const modalContainerClassNames =
		"fixed inset-0 flex items-end justify-center z-50 max-w-[1690px] mx-auto modal-bottom";
	const modalBorderClassNames = `modal-box min-w-wideModal border-0  px-2 lg:px-10 h-[95dvh] p-0 ${
		isMenuOpen ? "overflow-hidden pt-0" : "py-8"
	} ${hasBorder ? `border-${borderColor}` : "border-transparent"}`;
	const modalOverLayClassNames =
		"fixed inset-0 bg-gray-300 z-40 hover:cursor-pointer";
	const slideOutMenuClassNames = `slide-out-menu bg-white w-1/2 min-w-[300px] max-w-[500px] absolute top-0 left-0 z-40 z-[9999]`;
	const overlayClassNames = `bg-black absolute z-1 w-full left-0 top-0 cursor-pointer z-[9989]`;
	const overlayHeight = "100%";
	const menuItemClassNames = "sticky min-h-full max-h-full mb-20";

	///// TEXT FROM FIELDS /////
	const eventLogTitle = fields?.eventLog?.heading?.label;

	///// HELPER FUNCTIONS /////
	const toggleMenu = () => {
		setIsEventLogOpen(!isEventLogOpen);
	};

	const handleButtonClick = (modalType: ModalType) => {
		setActiveModal(modalType);
	};

	const toggleViewAllNotes = () => {
		setIsViewAllNotesOpen(!isViewAllNotesOpen);
	};

	const handleRefetchData = () => {
		setRefetchData((prevState) => !prevState);
	};

	const handleClose = () => {
		setIsMenuOpen(false);
		setIsEventLogOpen(false);
		setIsViewAllNotesOpen(false);
		setActiveModal(null);
		onClose();
		clearLocalStorage(["uuid", "orders"]);
		document.body.classList.remove("overflow-hidden");
	};

	///// DATA INTO VARS /////
	const dateOrder = formatDateForDetailsBox(orderDetails?.dateOrder);
	const orderNumber = orderDetails?.number || "—";
	const orderStatus =
		approvalDecisions?.currentStage?.ApprovalTemplateStages?.name || "—";
	const total = formatCurrency(orderDetails?._totalPurchase || "0");
	const subTotal = formatCurrency(orderDetails?._subtotalPurchase || "0");
	const tax = formatCurrency(Number(orderDetails?.tax) || "0");
	const orderTotalLease = formatCurrency(orderDetails?._totalLease || "0.00");
	const shipping = formatCurrency(orderDetails?.shipping || "0");
	const contractorInfo = salesOrderNotes?.contractorInfo?.note
		? `Contractor, ${salesOrderNotes?.contractorInfo?.note}`
		: "—";

	const currencyCharacter =
		orderDetails?.currency?.symbol ||
		fields?.settings?.currencyCharacter ||
		"";

	/////// FIELDS /////
	// TODO: this needs to come from the backend
	const checkIfApproveButtonIsEnabled = () => {
		const isStepOne =
			approvalDecisions?.currentStage?.ApprovalTemplateStages?.step ===
			"1";

		if (
			isStepOne &&
			(hostName === "COMPASS" || hostName === "COMPASSCANADA")
		) {
			const stepTwo = approvalDecisions?.stages?.find(
				(stage) => stage.ApprovalTemplateStages.step === "2",
			);
			const doesStepTwoHaveAssignedUser =
				stepTwo && stepTwo.AssignedTo.uuid !== null;

			if (
				(hostName === "COMPASS" || hostName === "COMPASSCANADA") &&
				isStepOne &&
				doesStepTwoHaveAssignedUser
			) {
				return true;
			} else {
				return false;
			}
		} else if (hostName === "QUAD") {
			if (purchaseOrderDetails?.uuid) {
				return true;
			} else {
				return false;
			}
		} else {
			return true;
		}
	};

	const isApproveButtonEnabled = checkIfApproveButtonIsEnabled() as boolean;

	const notificationEmail =
		orderDetails?.salesOrderEmailAddresses?.map(
			(email: { emailAddress: string }) => email.emailAddress ?? "—",
		) ?? [];

	const submittedByUsers = orderDetails?.createdByUser?._name || "";

	const approvalOrderDetailsProps = {
		fields,
		notificationEmail,
		reasonForRequest: salesOrderNotes?.reasonForRequest,
		shippingInstructions: salesOrderNotes?.shippingInstructions,
		orderSummary: {
			tax,
			total,
			shipping,
			subTotal,
		},
		orderTotalLease,
		aclPermissions,
		orderStatus,
		hostName,
		dateOrder,
		submittedByUsers,
		orderNumber,
		contractorInfo,
	};

	let skipManagerApprovalEnabled = true;
	//If Total is > 5000 hide the skip manager approval button
	if (total && Number(total) > 5000) {
		skipManagerApprovalEnabled = false;
	}

	///// SETTINGS FROM FIELDS /////
	const isNotesEnabled = fields?.approvalNotes?.isEnabled;

	return (
		fields && (
			<AnimatePresence>
				{isOpen && (
					<>
						<motion.div
							className={modalContainerClassNames}
							onClick={handleClose}
							initial={slideUpModalVariants.initial()}
							animate={slideUpModalVariants.animate()}
							exit={slideUpModalVariants.exit()}
							transition={slideUpModalVariants.transition}
						>
							<div
								className="min-w-full"
								onClick={(e) => e.stopPropagation()}
							>
								<div
									className={modalBorderClassNames}
									data-testid="approval-modal"
								>
									<motion.div>
										{showLoadingState ? (
											<div
												className="flex items-center justify-center h-svh "
												data-testid="approval-modal-loading"
											>
												<LoadingMessage message={""} />
											</div>
										) : showErrorMessage ? (
											<div className="flex items-center justify-center h-svh">
												An error took place.
											</div>
										) : (
											<div ref={modalRef}>
												<div>
													<ApprovalHeader
														dateOrder={dateOrder}
														orderNumber={
															orderNumber
														}
														orderStatus={
															orderStatus
														}
														handleButtonClick={
															handleButtonClick
														}
														handleClose={
															handleClose
														}
														toggleMenu={toggleMenu}
														fields={fields}
														aclPermissions={
															aclPermissions
														}
														skipManagerApprovalEnabled={
															skipManagerApprovalEnabled
														}
														isApproveButtonEnabled={
															isApproveButtonEnabled
														}
													/>
													{activeUuid &&
														isNotesEnabled && (
															<ApprovalNotesSection
																adminNotes={
																	salesOrderNotes?.adminNotes as AdminSalesOrderNote[]
																}
																activeUuid={
																	activeUuid
																}
																toggleViewAllNotes={
																	toggleViewAllNotes
																}
																setRefetchData={
																	setRefetchData
																}
															/>
														)}
													<section
														className="flex flex-col bg-white rounded-xl border border-gray-200 p-8"
														data-testid="approval-modal-content"
													>
														<ApprovalOrderDetails
															{...approvalOrderDetailsProps}
															activeUuid={
																activeUuid as string
															}
															orderDetails={
																orderDetails
															}
															approvalDecisions={
																approvalDecisions
															}
															currencyCharacter={
																currencyCharacter
															}
														/>

														<div className="border-b border-gray-200 w-full mt-3 mb-2"></div>
														<ApprovalOrderItems
															fields={fields}
															selectedBundles={
																selectedBundles
															}
															selectedSingleItems={
																selectedSingleItems
															}
															hostName={hostName}
															currencyCharacter={
																currencyCharacter
															}
														/>
													</section>
												</div>

												{activeModal ===
													"enterPoButton" &&
												activeUuid ? (
													<SupplyPortal containerId="portal-root">
														<EnterPoNumberModal
															isOpen={true}
															onClose={() =>
																setActiveModal(
																	null,
																)
															}
															children={null}
															activeUuid={
																activeUuid
															}
															modalSelected={
																activeModal
															}
															setRefetchData={
																setRefetchData
															}
															refetchData={
																refetchData
															}
															updateRowData={
																updateRowData
															}
															setDeactivatedQuickMenuIds={
																setDeactivatedQuickMenuIds ||
																[]
															}
															handleBigModalClose={
																handleClose
															}
															onUpdate={
																handleRefetchData
															}
															fields={
																fields?.enterPoNumberModal
															}
														/>
													</SupplyPortal>
												) : (
													activeUuid &&
													activeModal && (
														<SupplyPortal containerId="portal-root">
															<ApprovalDecisionsModal
																isOpen={true}
																onClose={() =>
																	setActiveModal(
																		null,
																	)
																}
																modalSelected={
																	activeModal
																}
																setRefetchData={
																	setRefetchData
																}
																refetchData={
																	refetchData
																}
																updateRowData={
																	updateRowData
																}
																setDeactivatedQuickMenuIds={
																	setDeactivatedQuickMenuIds ||
																	[]
																}
																handleBigModalClose={
																	handleClose
																}
																onUpdate={
																	handleRefetchData
																}
																isApproveButtonEnabled={
																	isApproveButtonEnabled
																}
																currencyCharacter={
																	currencyCharacter
																}
																parentModalClose={
																	handleClose
																}
															/>
														</SupplyPortal>
													)
												)}
												{isViewAllNotesOpen &&
													activeUuid &&
													isNotesEnabled && (
														<SupplyPortal containerId="portal-root">
															<ApprovalNotesModal
																adminNotes={
																	salesOrderNotes?.adminNotes as AdminSalesOrderNote[]
																}
																activeUuid={
																	activeUuid
																}
																toggleViewAllNotes={
																	toggleViewAllNotes
																}
																setRefetchData={
																	setRefetchData
																}
															/>
														</SupplyPortal>
													)}
											</div>
										)}
									</motion.div>
									<>
										<AnimatePresence>
											{isEventLogOpen && (
												<motion.div
													initial={
														slideOutMenuVariants.initial
													}
													animate={
														slideOutMenuVariants.animate
													}
													exit={
														slideOutMenuVariants.exit
													}
													transition={
														slideOutMenuVariants.transition
													}
													className={
														slideOutMenuClassNames
													}
													style={{
														height: overlayHeight,
													}}
												>
													<>
														<div
															className={
																menuItemClassNames
															}
														>
															<EventLog
																title={
																	eventLogTitle
																}
																isOpen={
																	isEventLogOpen
																}
																parentData={
																	orderDetails
																}
																slug={
																	"sales-orders"
																}
																hasActivityToggle={
																	true
																}
															/>
														</div>
													</>
												</motion.div>
											)}
										</AnimatePresence>
										<AnimatePresence>
											{isEventLogOpen && (
												<motion.div
													initial={
														slideOutMenuOverlayVariant.initial
													}
													animate={
														slideOutMenuOverlayVariant.animate
													}
													exit={
														slideOutMenuOverlayVariant.exit
													}
													transition={
														slideOutMenuOverlayVariant.transition
													}
													onClick={toggleMenu}
													className={
														overlayClassNames
													}
													style={{
														height: overlayHeight,
													}}
												></motion.div>
											)}
										</AnimatePresence>
									</>
								</div>
							</div>
						</motion.div>

						<motion.div
							{...slideUpModalOverlayVariants}
							className={modalOverLayClassNames}
							onClick={handleClose}
						></motion.div>
					</>
				)}
			</AnimatePresence>
		)
	);
};

export default ApprovalsModal;
